import React, { useCallback, useEffect, useRef } from 'react';
import { ListChildComponentProps } from 'react-window';

interface MeasuredRowData {
    setRowHeight: (index: number, height: number) => void;
    renderRow: (index: number) => React.ReactNode;
}

type MeasuredRowProps = ListChildComponentProps<MeasuredRowData>;

interface RenderedRowProps {
    index: number;
    renderRow: (index: number) => React.ReactNode;
}

const RenderedRow = ({ index, renderRow }: RenderedRowProps) => <>{renderRow(index)}</>;

const MeasuredRow = ({ index, style, data }: MeasuredRowProps) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const { setRowHeight, renderRow } = data;

    const stableSetRowHeight = useCallback((height: number) => setRowHeight(index, height), [index, setRowHeight]);

    useEffect(() => {
        const container = rowRef.current;
        if (!container) return;

        // Observe the actual row content (first child) rather than the
        // react-window wrapper which has a fixed height from getItemSize.
        const child = container.firstElementChild;
        if (!child) return;

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
                if (height > 0) {
                    stableSetRowHeight(height);
                }
            }
        });
        observer.observe(child);
        return () => observer.disconnect();
    }, [stableSetRowHeight]);

    return (
        <div ref={rowRef} style={style}>
            <RenderedRow index={index} renderRow={renderRow} />
        </div>
    );
};

export default MeasuredRow;
